import { useState } from 'react';
import { TrashIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useAgentPrompts, useAssignPrompt, useRemovePrompt } from '../../hooks/useAgents';
import { DataTable, DataTableHead, DataTableBody, DataTableRow, DataTableCell, TableHeader } from '../ui/DataTable';

interface AgentPromptsListProps {
  agentId: string;
  /**
   * Opens the active prompt's versions (the Agents page's Prompt → History sub
   * tab, task 1312). When given, the active row links there.
   */
  onOpenHistory?: () => void;
}

export function AgentPromptsList({ agentId, onOpenHistory }: AgentPromptsListProps) {
  const { data: agentPrompts, isLoading, error } = useAgentPrompts(agentId);
  const assignPrompt = useAssignPrompt();
  const removePrompt = useRemovePrompt();
  const [removingId, setRemovingId] = useState<string | null>(null);

  const handleSetActive = async (promptId: string) => {
    try {
      await assignPrompt.mutateAsync({ agentId, promptId });
    } catch (error) {
      console.error('Failed to set active prompt:', error);
    }
  };

  const handleRemove = async (promptId: string) => {
    if (removingId === promptId) {
      try {
        await removePrompt.mutateAsync({ agentId, promptId });
        setRemovingId(null);
      } catch (error) {
        console.error('Failed to remove prompt:', error);
      }
    } else {
      setRemovingId(promptId);
    }
  };

  const handleCancelRemove = () => {
    setRemovingId(null);
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
          Assigned Prompts
        </h3>
        <div className="animate-pulse space-y-2">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-800 p-6">
        <p className="text-sm text-red-600 dark:text-red-400">
          Error loading assigned prompts. Please try again.
        </p>
      </div>
    );
  }

  if (!agentPrompts || agentPrompts.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
          Assigned Prompts
        </h3>
        <div className="text-center py-8">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No prompts have been assigned to this agent yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
          Assigned Prompts
        </h3>
      </div>

      <DataTable>
        <DataTableHead>
          <TableHeader>Name</TableHeader>
          <TableHeader className="w-24">Status</TableHeader>
          <TableHeader className="w-40">Actions</TableHeader>
        </DataTableHead>
        <DataTableBody>
          {agentPrompts.map((agentPrompt) => (
            <DataTableRow key={agentPrompt.id}>
              <DataTableCell>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {agentPrompt.prompt?.name || 'Unknown Prompt'}
                  </span>
                  <a
                    href={`/prompts/${agentPrompt.promptId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                    title="Open prompt in new tab"
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </a>
                </div>
              </DataTableCell>
              <DataTableCell>
                {agentPrompt.isActive ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                      Active
                    </span>
                    {onOpenHistory && (
                      <button
                        type="button"
                        onClick={onOpenHistory}
                        data-testid="agent-prompts-active-history"
                        className="text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                      >
                        Versions →
                      </button>
                    )}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    Inactive
                  </span>
                )}
              </DataTableCell>
              <DataTableCell>
                <div className="flex items-center space-x-2">
                  {!agentPrompt.isActive && (
                    <button
                      onClick={() => handleSetActive(agentPrompt.promptId)}
                      disabled={assignPrompt.isPending}
                      className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 disabled:opacity-50"
                    >
                      {assignPrompt.isPending ? 'Setting...' : 'Set Active'}
                    </button>
                  )}
                  {removingId === agentPrompt.promptId ? (
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleRemove(agentPrompt.promptId)}
                        disabled={removePrompt.isPending}
                        className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50"
                      >
                        {removePrompt.isPending ? 'Removing...' : 'Confirm'}
                      </button>
                      <button
                        onClick={handleCancelRemove}
                        className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleRemove(agentPrompt.promptId)}
                      className="text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400"
                      title="Remove prompt"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </DataTableCell>
            </DataTableRow>
          ))}
        </DataTableBody>
      </DataTable>
    </div>
  );
}
